import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = { code: "", discount_type: "percent", value: 10, min_order: 0, expiry: "", active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code) return toast.error("Enter a coupon code");
    try {
      await api.post("/admin/coupons", { ...form, value: Number(form.value), min_order: Number(form.min_order), expiry: form.expiry || null });
      toast.success("Coupon saved");
      setForm(EMPTY);
      load();
    } catch { toast.error("Could not save coupon"); }
  };

  const del = async (code) => {
    await api.delete(`/admin/coupons/${code}`);
    toast.success("Coupon deleted");
    load();
  };

  const inputCls = "w-full bg-transparent border border-[var(--line)] px-3 py-2.5 outline-none text-[var(--bronze)] focus:border-[var(--olive)] transition-colors";

  return (
    <div data-testid="admin-coupons">
      <h1 className="font-serif text-4xl text-[var(--bronze)] mb-2">Coupons & Offers</h1>
      <p className="text-[var(--olive)] mb-8">Create discount codes for your customers.</p>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="border border-[var(--line)] p-6 h-fit">
          <h2 className="font-serif text-xl text-[var(--bronze)] mb-5">New Coupon</h2>
          <div className="space-y-4">
            <div><label className="uplabel text-[0.6rem] block mb-1.5">Code</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input" className={inputCls} placeholder="FESTIVE20" /></div>
            <div><label className="uplabel text-[0.6rem] block mb-1.5">Type</label>
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger className="border-[var(--line)] bg-transparent text-[var(--bronze)]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FBF7DE] border-[var(--line)]"><SelectItem value="percent">Percentage Off</SelectItem><SelectItem value="flat">Flat Off (₹)</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="uplabel text-[0.6rem] block mb-1.5">Value {form.discount_type === "percent" ? "(%)" : "(₹)"}</label><input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} /></div>
            <div><label className="uplabel text-[0.6rem] block mb-1.5">Min. Order (₹)</label><input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className={inputCls} /></div>
            <div><label className="uplabel text-[0.6rem] block mb-1.5">Expiry (optional)</label><input type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} className={inputCls} /></div>
            <button onClick={create} data-testid="create-coupon-btn" className="btn-primary w-full uplabel py-3 flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Create Coupon</button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-3">
          {coupons.length === 0 ? <div className="border border-[var(--line)] p-12 text-center text-[var(--olive)]">No coupons yet.</div> :
            coupons.map((c) => (
              <div key={c.code} data-testid={`coupon-${c.code}`} className="border border-[var(--line)] p-5 flex items-center justify-between">
                <div>
                  <div className="font-serif text-xl text-[var(--bronze)]">{c.code}</div>
                  <div className="text-sm text-[var(--olive)]">
                    {c.discount_type === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}
                    {c.min_order > 0 && ` · min ${inr(c.min_order)}`}
                    {c.expiry && ` · expires ${new Date(c.expiry).toLocaleDateString("en-IN")}`}
                  </div>
                </div>
                <button onClick={() => del(c.code)} data-testid={`delete-coupon-${c.code}`} className="text-[var(--olive)] hover:text-[var(--maroon)]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
